import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Brain, TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";
import { BUSINESS_SECTORS } from "@/utils/businessSectors";

interface BuyerPreferences {
  budget: number;
  sectors: string[];
  location: string;
  goals: string;
  experience: string;
}

interface MatchingResult {
  listingId: string;
  analysis: {
    matchScore: number;
    reasons: string[];
    recommendations: string[];
    riskFactors: string[];
    nextSteps: string[];
  };
}

export function AIBusinessMatching() {
  const [buyerPreferences, setBuyerPreferences] = useState<BuyerPreferences>({
    budget: 0,
    sectors: [],
    location: '',
    goals: '',
    experience: ''
  });
  const [listingId, setListingId] = useState('');
  const [result, setResult] = useState<MatchingResult | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleAnalyze = async () => {
    if (!listingId.trim()) {
      toast({
        title: "Error",
        description: "Please enter a listing ID",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('ai-business-matching', {
        body: {
          listingId: listingId.trim(),
          buyerPreferences
        }
      });

      if (error) throw error;
      setResult(data);
      
      toast({
        title: "Analysis Complete",
        description: `Match score: ${data.analysis.matchScore}%`
      });
    } catch (error) {
      console.error('Error analyzing match:', error);
      toast({
        title: "Error",
        description: "Failed to analyze business match",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSectorChange = (sector: string) => {
    setBuyerPreferences(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sector)
        ? prev.sectors.filter(s => s !== sector)
        : [...prev.sectors, sector]
    }));
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600 bg-green-50";
    if (score >= 60) return "text-yellow-600 bg-yellow-50";
    return "text-red-600 bg-red-50";
  };

  const commonSectors = [
    "Technology", "Healthcare", "Retail", "Manufacturing", "Hospitality",
    "Professional Services", "Construction", "Food & Beverage", "Transport"
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Brain className="h-6 w-6" />
          AI Business Matching
        </h2>
        <p className="text-muted-foreground">
          Use AI to analyze how well a business matches buyer preferences
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Buyer Preferences</CardTitle>
            <CardDescription>Configure the buyer's requirements and preferences</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="budget">Budget ($)</Label>
              <Input
                id="budget"
                type="number"
                value={buyerPreferences.budget || ''}
                onChange={(e) => setBuyerPreferences(prev => ({
                  ...prev,
                  budget: parseInt(e.target.value) || 0
                }))}
                placeholder="500000"
              />
            </div>

            <div>
              <Label>Preferred Sectors</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {BUSINESS_SECTORS.map(sector => (
                  <Badge
                    key={sector}
                    variant={buyerPreferences.sectors.includes(sector) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => handleSectorChange(sector)}
                  >
                    {sector}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="location">Location Preference</Label>
              <Input
                id="location"
                value={buyerPreferences.location}
                onChange={(e) => setBuyerPreferences(prev => ({
                  ...prev,
                  location: e.target.value
                }))}
                placeholder="Sydney, NSW"
              />
            </div>

            <div>
              <Label htmlFor="goals">Investment Goals</Label>
              <Textarea
                id="goals"
                value={buyerPreferences.goals}
                onChange={(e) => setBuyerPreferences(prev => ({
                  ...prev,
                  goals: e.target.value
                }))}
                placeholder="Looking for a stable business with growth potential..."
                className="min-h-[80px]"
              />
            </div>

            <div>
              <Label htmlFor="experience">Experience Level</Label>
              <Input
                id="experience"
                value={buyerPreferences.experience}
                onChange={(e) => setBuyerPreferences(prev => ({
                  ...prev,
                  experience: e.target.value
                }))}
                placeholder="First-time buyer, 10+ years in industry, etc."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Business Analysis</CardTitle>
            <CardDescription>Enter a listing ID to analyze the match</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="listingId">Listing ID</Label>
              <Input
                id="listingId"
                value={listingId}
                onChange={(e) => setListingId(e.target.value)}
                placeholder="Enter listing UUID"
              />
            </div>

            <Button 
              onClick={handleAnalyze} 
              disabled={loading}
              className="w-full"
            >
              {loading ? "Analyzing..." : "Analyze Match"}
            </Button>

            {result && (
              <div className="space-y-4 pt-4 border-t">
                <div className={`p-4 rounded-lg ${getScoreColor(result.analysis.matchScore)}`}>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    <span className="font-semibold">Match Score: {result.analysis.matchScore}%</span>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Match Reasons
                  </h4>
                  <ul className="space-y-1">
                    {result.analysis.reasons.map((reason, index) => (
                      <li key={index} className="text-sm text-muted-foreground">
                        • {reason}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    Risk Factors
                  </h4>
                  <ul className="space-y-1">
                    {result.analysis.riskFactors.map((risk, index) => (
                      <li key={index} className="text-sm text-muted-foreground">
                        • {risk}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Recommendations</h4>
                  <ul className="space-y-1">
                    {result.analysis.recommendations.map((rec, index) => (
                      <li key={index} className="text-sm text-muted-foreground">
                        • {rec}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Next Steps</h4>
                  <ul className="space-y-1">
                    {result.analysis.nextSteps.map((step, index) => (
                      <li key={index} className="text-sm text-muted-foreground">
                        • {step}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}